import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { canonAPI } from '../lib/api';
import {
  BookOpen,
  MessageSquare,
  Scale,
  GraduationCap,
  Shield,
  Heart,
  Eye,
  RotateCcw,
  ArrowRight,
  CheckCircle2,
  Share2
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';

const principleIcons = {
  dignity: Shield,
  liberty: MessageSquare,        // or Bird if you import it
  "non-harm": Heart,
  proportionality: Scale,
  accountability: Eye,
  reversibility: RotateCcw,
  separation: BookOpen,          // pick any icon you like
  transparency: Eye,
};

const features = [
  {
    icon: BookOpen,
    title: 'Library',
    description: 'Browse the complete canon by principles, titles, and articles',
    link: '/library',
    color: 'text-blue-500',
  },
  {
    icon: MessageSquare,
    title: 'Ask Canon',
    description: 'AI-powered answers with mandatory citations from the canon',
    link: '/chat',
    color: 'text-green-500',
  },
  {
    icon: Scale,
    title: 'Case Tool',
    description: 'Analyze scenarios using the Four-Scale test',
    link: '/case',
    color: 'text-amber-500',
  },
  {
    icon: GraduationCap,
    title: 'Learn',
    description: 'Interactive modules on critical thinking and proportionality',
    link: '/learn',
    color: 'text-purple-500',
  },
];

const commitments = [
  'commitmentsList.0',
  'commitmentsList.1',
  'commitmentsList.2',
  'commitmentsList.3',
  'commitmentsList.4',
  'commitmentsList.5',
];

export default function HomePage() {
  const [principles, setPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const handleShare = async () => {
    const shareText = t('share.homeText');
    const shareUrl = "https://synthconst.com";

    const copyFallback = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('share.copied'));
      } catch {
        toast.error(t('share.failed'));
      }
    };

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SynthConst',
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyFallback();
        }
      }
    } else {
      copyFallback();
    }
  };

  useEffect(() => {
    async function loadPrinciples() {
      try {
        const response = await canonAPI.getPrinciples();

        // Debug what we actually received
        console.log("Principles response:", {
          url: response?.config?.url,
          status: response?.status,
          dataType: typeof response?.data,
          isArray: Array.isArray(response?.data),
          dataPreview: response?.data,
        });

        const data = response?.data;

        if (!Array.isArray(data)) {
          console.error("Expected principles to be an array, got:", data);
          setPrinciples([]); // prevent crash
          return;
        }

        setPrinciples(data);
      } catch (error) {
        console.error("Failed to load principles:", error);
        console.error("Requested URL was:", error.config?.url);
        console.error("Response status:", error.response?.status);
        console.error("Response data:", error.response?.data);
        setPrinciples([]); // prevent crash
      } finally {
        setLoading(false);
      }
    }

    loadPrinciples();
  }, [t]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto text-center">
          <Badge variant="outline" className="mb-6">
            {t('home.badge')}
          </Badge>
          <h1 className="hero-title mb-6" data-testid="hero-title">
            {t('home.title')}
            <br />
            <span className="text-accent">{t('home.subtitle')}</span>
          </h1>
          <p className="hero-subtitle">
            {t('home.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/library">
              <Button size="lg" className="w-full sm:w-auto" data-testid="cta-library">
                <BookOpen className="mr-2 h-4 w-4" />
                {t('home.exploreLibrary')}
              </Button>
            </Link>
            <Link to="/chat">
              <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="cta-chat">
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('home.askQuestion')}
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="w-full sm:w-auto text-accent border-accent hover:bg-accent/10 hover:text-accent" onClick={handleShare}>
              <Share2 className="mr-2 h-4 w-4" />
              {t('share.homeButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-title">{t('home.corePrinciples')}</h2>
            <p className="section-subtitle">
              {t('home.corePrinciplesSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="principles-grid">
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="loading-pulse h-32 rounded-lg" />
              ))
            ) : (
              principles.map((principle) => {
                const Icon = principleIcons[principle.name.toLowerCase()] || Shield;
                return (
                  <Link
                    key={principle.name}
                    to={`/library?principle=${principle.name.toLowerCase()}`}
                    data-testid={`principle-${principle.name.toLowerCase()}`}
                  >
                    <Card className="principle-card h-full text-center">
                      <CardContent className="pt-6">
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                          <Icon className="h-6 w-6 text-accent" />
                        </div>
                        <h3 className="font-semibold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                          {principle.name}
                        </h3>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {principle.description}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-title">{t('home.howToUse')}</h2>
            <p className="section-subtitle">
              {t('home.howToUseSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="features-grid">
            {features.map((feature) => (
              <Link key={feature.title} to={feature.link} data-testid={`feature-${feature.title.toLowerCase().replace(' ', '-')}`}>
                <Card className="h-full card-hover">
                  <CardHeader>
                    <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center mb-2 ${feature.color}`}>
                      <feature.icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{t(`home.features.${feature.title.toLowerCase().split(' ')[0]}.title`)}</CardTitle>
                    <CardDescription>{t(`home.features.${feature.title.toLowerCase().split(' ')[0]}.desc`)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <span className="text-sm text-accent inline-flex items-center">
                      {t('home.features.explore')} <ArrowRight className="ml-1 h-3 w-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="section-title">{t('home.commitmentsTitle')}</h2>
              <p className="section-subtitle">
                {t('home.commitmentsSub')}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="commitments-grid">
              {commitments.map((commitment) => (
                <div
                  key={commitment}
                  className="flex items-center space-x-2 text-sm"
                  data-testid="commitment-item"
                >
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>{t(`home.${commitment}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto text-center">
          <h2 className="section-title mb-4">{t('home.ctaTitle')}</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            {t('home.ctaSub')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/library">
              <Button size="lg" data-testid="footer-cta-library">
                {t('home.ctaLibrary')}
              </Button>
            </Link>
            <Link to="/learn">
              <Button size="lg" variant="outline" data-testid="footer-cta-learn">
                {t('home.ctaLearn')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
